import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import { useOrganization } from '../context/OrganizationContext.jsx';
import billingService from '../services/billingService';

function isEnabled(value) {
  if (value === true || value === 'unlimited') return true;
  if (value === null || value === -1) return true;
  if (typeof value === 'number') return value > 0;
  return false;
}

function toEntityId(value) {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (value._id) return toEntityId(value._id);
  if (value.id) return toEntityId(value.id);
  return null;
}

function normalizeLimitsPayload(data) {
  return data?.effectiveLimits || data?.limits || data || {};
}

export function usePlanEntitlements({ workspace = 'personal' } = {}) {
  const { currentUser } = useAuth();
  const { currentOrganization } = useOrganization();
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const organizationId = workspace === 'organization'
    ? toEntityId(currentOrganization)
      || toEntityId(currentUser?.organizationId)
      || toEntityId(currentUser?.organization)
      || null
    : null;

  const loadLimits = useCallback(async () => {
    if (!currentUser) {
      setLimits(null);
      setLoading(false);
      return;
    }

    if (workspace === 'organization' && !organizationId) {
      setLimits(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await billingService.getEffectiveLimits(organizationId);
      setLimits(normalizeLimitsPayload(data));
    } catch (err) {
      setError(err);
      setLimits(null);
    } finally {
      setLoading(false);
    }
  }, [currentUser, organizationId, workspace]);

  useEffect(() => {
    loadLimits();
  }, [loadLimits]);

  return useMemo(() => ({
    limits,
    loading,
    error,
    organizationId,
    workspace,
    refresh: loadLimits,
    hasFeature: (featureKey) => isEnabled(limits?.[featureKey]),
    getLimit: (itemId) => limits?.[itemId]
  }), [limits, loading, error, organizationId, workspace, loadLimits]);
}

export default usePlanEntitlements;
